import { useEffect, useRef } from "react";
import { addReadingTime } from "../api";

/**
 * The web's reading clock (ADR-0190 §6), counting by the app's rule, written
 * down once in narration: a second is **listen** while audio plays, otherwise
 * **read** while the chapter is on screen and was touched in the last two
 * minutes. It sends small increments; the server clamps their sum and picks
 * the day.
 */
export class ReadingClock {
  private read = 0;
  private listen = 0;
  private lastTouch: number;

  constructor(
    private readonly send: (read: number, listen: number) => Promise<void>,
    private readonly now: () => number = Date.now,
    private readonly idleMs = 2 * 60_000,
    private readonly flushEvery = 60,
  ) {
    // Opening a chapter is the first touch.
    this.lastTouch = now();
  }

  touched() {
    this.lastTouch = this.now();
  }

  /** One second passed: playing is the audio's state, visible the page's. */
  tick(playing: boolean, visible: boolean) {
    if (playing) this.listen++;
    else if (visible && this.now() - this.lastTouch < this.idleMs) this.read++;
    if (this.read + this.listen >= this.flushEvery) void this.flush();
  }

  /**
   * Sends what has been counted. A failed send is dropped, as in the app: the
   * measure is "about N minutes", and a retry could count a minute twice.
   */
  async flush() {
    const read = this.read;
    const listen = this.listen;
    this.read = 0;
    this.listen = 0;
    if (read + listen <= 0) return;
    try {
      await this.send(read, listen);
    } catch {
      // dropped
    }
  }
}

const touches = ["pointerdown", "keydown", "wheel", "scroll", "touchstart"] as const;

/**
 * useReadingClock counts a signed-in reader's time on the chapter page, for
 * the book it belongs to. audio is the page's one element (sharedAudio.ts).
 */
export function useReadingClock(
  bookID: string | undefined,
  audio: HTMLAudioElement | null,
  enabled: boolean,
) {
  const audioRef = useRef(audio);
  audioRef.current = audio;
  useEffect(() => {
    if (!enabled || !bookID) return;
    const clock = new ReadingClock((read, listen) => addReadingTime(bookID, read, listen));
    const touched = () => clock.touched();
    for (const name of touches)
      window.addEventListener(name, touched, { passive: true, capture: true });
    const timer = window.setInterval(() => {
      const el = audioRef.current;
      clock.tick(!!el && !el.paused && !el.ended, document.visibilityState === "visible");
    }, 1000);
    const hidden = () => {
      if (document.visibilityState === "hidden") void clock.flush();
    };
    document.addEventListener("visibilitychange", hidden);
    return () => {
      window.clearInterval(timer);
      for (const name of touches) window.removeEventListener(name, touched, { capture: true });
      document.removeEventListener("visibilitychange", hidden);
      void clock.flush();
    };
  }, [bookID, enabled]);
}
